import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { QueryClient, queryOptions } from '@tanstack/angular-query-experimental';
import { environment } from '../../../environments/environment';
import { setSignedInCustomer } from '../monitoring/sentry-reporting';
import { Person } from '../../shared/services/reservation.service';
import {
  CUSTOMER_PERSON_QUERY_KEY,
  RESERVATION_QUERY_KEY_ROOTS,
  WEBSHOP_SESSION_QUERY_KEY,
} from '../../shared/services/reservation-query-keys';
import { legacyErrorBody } from '../../shared/utils/legacy-error-body';

export interface WebshopSession {
  isInternalUser: boolean;
  reservationId: number | null;
  reservationIdsForReview: number[];
  signedInCustomerPersonId: number | null;
  tripId: number | null;
}

export interface RequestTokenResponse {
  uid: string;
}

export interface AuthenticateResponse {
  signedInCustomerPersonId: number;
}

export class CustomerAccountMissingError extends Error {
  constructor() {
    super('No customer account exists for the verified phone number.');
    this.name = 'CustomerAccountMissingError';
  }
}

export class InvalidLoginCodeError extends Error {
  constructor() {
    super('The provided login code is invalid.');
    this.name = 'InvalidLoginCodeError';
  }
}

export class PhoneNumberInUseError extends Error {
  constructor() {
    super('A person already exists with this phone number.');
    this.name = 'PhoneNumberInUseError';
  }
}

/** Everything `POST /api/v1/webshopSession/customerAccount` requires to create
 *  an account. Every field below is validated server-side; omitting one is a
 *  400 or a 409, never a silently half-made account. */
export interface CreateCustomerAccountInput {
  /** The number the code was sent to — `requestToken`'s `uid`. Stored verbatim
   *  as the new Person's `phoneNumber`, which is also what a later sign-in has
   *  to match, so it must be the normalized form the login dialog sends. */
  uid: string;
  /** The code from the SMS. Omitted when this session already verified this
   *  number — the backend records a verified number even on a sign-in that
   *  failed for want of an account, which is exactly the case that sends a
   *  customer here. */
  token?: string;
  firstName: string;
  lastName: string;
  email: string;
  city: string;
  /** Must be true; the backend 409s otherwise. */
  privacyPolicy: boolean;
  /** Consent to the marketing newsletter. Inverted into the Person's
   *  `disallowPromotionalEmail` by the backend. */
  newsletter: boolean;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private queryClient = inject(QueryClient);

  webshopSessionOptions() {
    return queryOptions({
      queryKey: [WEBSHOP_SESSION_QUERY_KEY] as const,
      queryFn: () => this.fetchWebshopSession(),
    });
  }

  private async fetchWebshopSession(): Promise<WebshopSession> {
    const session = await firstValueFrom(
      this.http.get<WebshopSession>(`${environment.erpBaseUrl}/api/v1/webshopSession`, {
        params: new HttpParams().set('source', 'IMT'),
        withCredentials: true,
      }),
    );
    // The only place a customer already signed in from an earlier visit is
    // recognised — the sign-in/sign-out calls below only see the transitions
    // they make themselves, and a returning customer makes none of them.
    setSignedInCustomer(session.signedInCustomerPersonId);
    return session;
  }

  /** One-shot read of the active reservation id from the webshop session. The
   *  session is the single source of truth for which reservation the customer
   *  is booking; the editor uses this to resume that reservation on refresh.
   *
   *  Goes through the query cache with `staleTime: 0`: the answer is always the
   *  backend's current one, but a `GET /webshopSession` the pages' own session
   *  query already has in flight is joined rather than duplicated. On a cold
   *  load that duplicate was the second of the two requests racing the
   *  interceptor's retry-with-session path (see {@link createSession}). */
  getActiveReservationId(): Promise<number | null> {
    return this.queryClient
      .fetchQuery({ ...this.webshopSessionOptions(), staleTime: 0 })
      .then((session) => session.reservationId);
  }

  private createSessionInFlight: Promise<unknown> | null = null;

  /** A cookie-less request mints a brand-new session token server-side
   *  (`token-session`), so two `createSession()` calls in flight at once
   *  produce two different `Set-Cookie`s that stomp each other in the
   *  browser's cookie jar — whichever lands last silently orphans whatever
   *  the other call's session had already done (e.g. a just-created
   *  reservation), and the next request under the surviving cookie 403s.
   *  Any two requests can race the interceptor's retry-with-session path
   *  independently (until it joined the cached query, `getActiveReservationId()`
   *  did exactly that against the pages' `webshopSessionOptions()` query on
   *  every cold load). Sharing one in-flight promise makes every concurrent
   *  caller settle on the same session instead of racing to mint their own. */
  createSession(): Promise<unknown> {
    if (!this.createSessionInFlight) {
      this.createSessionInFlight = this.postSession().finally(() => {
        this.createSessionInFlight = null;
      });
    }
    return this.createSessionInFlight;
  }

  private async postSession(): Promise<unknown> {
    const response = await firstValueFrom(
      this.http.post(
        `${environment.erpBaseUrl}/api/v1/webshopSession`,
        {},
        { withCredentials: true },
      ),
    );
    // Minting a session changes what `GET /webshopSession` returns (internal-user
    // flag, active reservation), so the cached query has to be refreshed. Started
    // but deliberately not awaited: the only caller of `createSession()` is the
    // 202-retry in `missingWebshopSessionInterceptor`, and on a cold load the
    // query being refreshed is the very one whose GET is parked in that retry.
    // TanStack refetches an in-flight query by handing back its pending promise,
    // so awaiting here waits for a response that can only arrive once we return —
    // a deadlock that leaves the editor spinning on "Päivitetään matkan tietoja…"
    // forever. Nothing is lost by not waiting: the parked GET re-runs under the
    // new session and its own result lands in the cache.
    void this.queryClient
      .invalidateQueries({ queryKey: [WEBSHOP_SESSION_QUERY_KEY] })
      .catch(() => undefined);
    return response;
  }

  requestLoginToken(phoneNumber: string): Promise<RequestTokenResponse> {
    return firstValueFrom(
      this.http.post<RequestTokenResponse>(
        `${environment.erpBaseUrl}/api/v1/customerAccount/requestToken`,
        { phoneNumber, delivery: 'sms' },
        { withCredentials: true },
      ),
    );
  }

  async authenticateCustomerAccount(uid: string, token: string): Promise<AuthenticateResponse> {
    try {
      const response = await firstValueFrom(
        this.http.put<AuthenticateResponse>(
          `${environment.erpBaseUrl}/api/v1/webshopSession/customerAccount`,
          { uid, token },
          { withCredentials: true },
        ),
      );
      setSignedInCustomer(response.signedInCustomerPersonId);
      await this.invalidateReservations();
      await this.queryClient.invalidateQueries({ queryKey: [WEBSHOP_SESSION_QUERY_KEY] });
      return response;
    } catch (error) {
      if (error instanceof HttpErrorResponse && error.status === 403) {
        // `HTTPError(403, {...})` serialises as `{name, statusCode, data}` —
        // the flag is under `data`, same as the webshop reads it
        // (`customer-account/login/login.js`). Read off the top level it never
        // matched, and a correct code on a number with no account was reported
        // as a bad code. `legacyErrorBody` is what does that unwrapping (and
        // the null/type guards around it) for every other caller in the funnel.
        if (legacyErrorBody(error)?.['missingCustomerAccount']) {
          throw new CustomerAccountMissingError();
        }
        throw new InvalidLoginCodeError();
      }
      throw error;
    }
  }

  /**
   * Creates a customer account for a phone number this session has proved the
   * customer holds, and signs them in — the backend does both in one call, so
   * there is no window where the account exists but nobody is logged into it.
   *
   * `token` may be omitted when the number was already verified in this session;
   * see {@link CreateCustomerAccountInput}.
   */
  async createCustomerAccount(input: CreateCustomerAccountInput): Promise<AuthenticateResponse> {
    try {
      const response = await firstValueFrom(
        this.http.post<AuthenticateResponse>(
          `${environment.erpBaseUrl}/api/v1/webshopSession/customerAccount`,
          input,
          { withCredentials: true },
        ),
      );
      setSignedInCustomer(response.signedInCustomerPersonId);
      await this.invalidateReservations();
      await this.queryClient.invalidateQueries({ queryKey: [WEBSHOP_SESSION_QUERY_KEY] });
      await this.queryClient.invalidateQueries({ queryKey: [CUSTOMER_PERSON_QUERY_KEY] });
      return response;
    } catch (error) {
      if (error instanceof HttpErrorResponse) {
        // The number was taken between the code being sent and this call — or
        // the customer already had an account and came the wrong way round.
        if (error.status === 409 && legacyErrorBody(error)?.['phoneNumber']) {
          throw new PhoneNumberInUseError();
        }
        if (error.status === 403) {
          throw new InvalidLoginCodeError();
        }
      }
      throw error;
    }
  }

  getPerson(personId: number): Promise<Person> {
    return firstValueFrom(
      this.http.get<Person>(`${environment.erpBaseUrl}/api/v1/persons/${personId}`, {
        withCredentials: true,
      }),
    );
  }

  /** The signed-in customer's own Person row — their name for the top bar, and
   *  their loyalty balance for the price card. Read separately from the
   *  reservation because nothing links the two until the passenger step: on the
   *  hub, `reservation.customerPerson` is empty even for a customer who signed
   *  in a minute ago.
   *
   *  Keyed by person id so signing out and back in as someone else can't serve
   *  the previous customer's row. Off while nobody is signed in. */
  customerPersonOptions(personId: number | null) {
    return queryOptions({
      queryKey: [CUSTOMER_PERSON_QUERY_KEY, personId] as const,
      queryFn: () => this.getPerson(personId!),
      enabled: personId != null,
    });
  }

  /** Ends the customer session (the funnel's own reservation session survives —
   *  only the sign-in goes). Invalidates the cached session so every `isSignedIn`
   *  in the app flips in the same tick. */
  async signOut(): Promise<void> {
    await firstValueFrom(
      this.http.post(
        `${environment.erpBaseUrl}/api/v1/customerAccount/logout`,
        {},
        { withCredentials: true },
      ),
    );
    // Cleared here rather than left to the session refetch below: invalidation
    // only refetches queries something is currently observing, and an event
    // raised after sign-out must never still carry the previous customer.
    setSignedInCustomer(null);
    await this.invalidateReservations();
    await this.queryClient.invalidateQueries({ queryKey: [WEBSHOP_SESSION_QUERY_KEY] });
    await this.queryClient.invalidateQueries({ queryKey: [CUSTOMER_PERSON_QUERY_KEY] });
  }

  /**
   * Re-fetches every cached reservation, because signing in or out changed it
   * server-side.
   *
   * `PUT`/`POST /webshopSession/customerAccount` end by calling
   * `transferActiveReservationToCustomerAccount`, which sets the reservation's
   * `customerPersonId` to the account's person. The bonus rows are derived from
   * that column matching the signed-in customer (`deriveReservationView`), so
   * until the reservation is refetched a customer who signs in on `/summary` or
   * `/payment` sees no bonus earned and no bonus used — the session signal
   * flips, the reservation behind it does not.
   *
   * Done here rather than at each login site because the login dialog is
   * reachable from the navbar on every step, and only this layer knows the
   * server-side link happened. The steps' own caches are pinned
   * (`staleTime: Infinity`, `refetchOnMount: 'always'`), so nothing else would
   * ever pull the new row short of remounting the component — which is exactly
   * why walking back to passenger-info and forward again "fixed" it.
   *
   * Awaited *before* the `webshopSession` invalidation by every caller, not
   * after: the payment step re-runs its prep flow when the signed-in customer
   * changes, and that flow reads the bonus off the reservation. Refreshing the
   * session first would fire it against the reservation this call is about to
   * replace, and it would post no bonus at all.
   */
  private async invalidateReservations(): Promise<void> {
    await Promise.all(
      RESERVATION_QUERY_KEY_ROOTS.map((root) =>
        this.queryClient.invalidateQueries({ queryKey: [root] }),
      ),
    );
  }
}
